import { Constants } from "./constants";

/**
 * Type definition for throttled and debounced callback functions.
 * Preserves the argument types while enforcing void return type.
 */
export type ThrottledCallback<TArgs extends unknown[]> = {
  (...args: TArgs): void;
  cancel(): void;
};

/**
 * Debounces a function to delay execution until after a wait period of inactivity.
 *
 * How it works:
 * - Each call resets the wait timer
 * - Function only executes after no calls have been made for the full wait period
 * - Useful for actions that should only happen after user stops an activity (e.g., typing, scrolling)
 *
 * Example: debounce(search, 300)
 * - Type at t=0ms: starts 300ms timer
 * - Type at t=100ms: resets timer, starts new 300ms timer
 * - Type at t=200ms: resets timer, starts new 300ms timer
 * - No typing for 300ms: executes search at t=500ms
 *
 * @param fn - The function to debounce
 * @param wait - Time in milliseconds to wait before executing (default: Constants.DEBOUNCE_DEFAULT)
 * @param immediate - If true, execute on the leading edge instead of trailing edge
 * @returns Debounced function
 */
const debounce = <TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  wait = Constants.DEBOUNCE_DEFAULT,
  immediate = false
): ThrottledCallback<TArgs> => {
  // Track the pending trailing execution timeout
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const cancel = (): void => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const debounced = (...args: TArgs): void => {
    const later = (): void => {
      timeoutId = null;
      if (!immediate) {
        fn(...args);
      }
    };
    const callNow = immediate && !timeoutId;
    cancel();
    timeoutId = setTimeout(later, wait);
    if (callNow) {
      fn(...args);
    }
  };

  debounced.cancel = cancel;
  return debounced;
};

/**
 * Throttles a function to execute at most once per delay period with trailing execution.
 *
 * How it works:
 * - First call: executes immediately
 * - Calls within throttle period: schedules a trailing execution at the end of the period
 * - Each new call cancels the previous scheduled execution and reschedules with the latest arguments
 * - Result: immediate response + guaranteed final execution with the most recent data
 *
 * Example: throttle(updateUI, 1000)
 * - Call at t=0ms: executes immediately
 * - Call at t=300ms: schedules for t=1000ms
 * - Call at t=600ms: cancels previous, reschedules for t=1000ms
 * - Call at t=1000ms: executes immediately (throttle window has passed)
 *
 * @param fn - The function to throttle
 * @param delay - Minimum time in milliseconds between executions
 * @returns Throttled function that ensures trailing execution
 */
const throttle = <TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delay: number
): ThrottledCallback<TArgs> => {
  // Track when the function was last executed (timestamp in ms)
  let lastCall = 0;
  // Track the pending trailing execution timeout
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const cancel = (): void => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const throttled = (...args: TArgs): void => {
    const now = Date.now();

    // Check if we're still within the throttle window
    if (now - lastCall < delay) {
      // Update the pending trailing execution with the latest args.
      // Cancel + reschedule so the trailing call always uses the most recent data,
      // but only one trailing timeout is ever pending at a time — we do NOT cancel
      // it on every call (that would push it forward indefinitely on a sustained stream).
      cancel();
      const wait = delay - (now - lastCall);
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        timeoutId = null;
        fn(...args);
      }, wait);
      return;
    }

    // Throttle window has passed — execute immediately and reset the window.
    // Cancel any pending trailing call since we're executing now.
    cancel();
    lastCall = now;
    fn(...args);
  };

  throttled.cancel = cancel;
  return throttled;
};

export { debounce, throttle };
